<script setup lang="ts">
import { ref, toRef, computed, watch } from "vue";
import { type InputProps } from "@phila/phila-ui-core";
import { useFieldErrors } from "@/composables/useFieldErrors";

interface VeeDropdownProps {
  modelValue?: string | undefined;
  rules?: string;
  placeholder?: string;
  label?: string;
  innerLabel?: boolean;
}

const props = withDefaults(defineProps<InputProps & VeeDropdownProps>(), {
  modelValue: undefined,
  rules: "",
  id: "",
  placeholder: "",
  label: "",
  innerLabel: false,
});

const emit = defineEmits(["update:modelValue", "change", "blur"]);
const name = toRef(props, "id");
const localValue = ref(props.modelValue);

// Dropdown keeps unrecognised attributes on its wrapper and never passes them
// to the <select>, so the error association is made against the rendered markup.
const control = ref<{ $el?: HTMLElement } | null>(null);
useFieldErrors(control);

const dropdownProps = computed(() => {
  const { modelValue: _, rules: __, id: ___, ...rest } = props;
  return rest;
});

watch(
  () => props.modelValue,
  (val) => {
    if (val !== localValue.value) localValue.value = val;
  },
);

watch(
  () => localValue.value,
  (val) => {
    emit("update:modelValue", val);
  },
);
</script>
<template>
  <Field
    v-slot="{ errorMessage, handleChange, handleBlur }"
    :name="name"
    :rules="rules"
    :model-value="localValue"
  >
    <dropdown
      :id="id"
      ref="control"
      v-model="localValue"
      v-bind="{ ...dropdownProps, ...$attrs }"
      :errors="errorMessage"
      @change="handleChange"
      @blur="handleBlur"
    />
  </Field>
</template>
